import React from 'react';

// A lock/unlock TOGGLE switch shared across the editable pages (Anesthesiologists, CRNAs, Credentials).
// Unlocked (editing) = switch to the right with GREEN beside it; locked = switch left with RED beside it.
// Clicking flips the state. `onChange` receives the NEXT locked value (true = now locked).

interface LockToggleProps {
  locked: boolean;
  onChange: (nextLocked: boolean) => void;
  unlockedLabel?: string;
  lockedLabel?: string;
  autoLockHint?: boolean;
}

const GREEN = '#1f9d55';
const RED = '#c23b3b';

const LockToggle: React.FC<LockToggleProps> = ({
  locked,
  onChange,
  unlockedLabel = 'Editing',
  lockedLabel = 'Locked',
  autoLockHint = true,
}) => {
  const editing = !locked;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={editing}
      onClick={() => onChange(!locked)}
      title={editing ? `Editing — click to lock${autoLockHint ? ' (auto-locks after 30s)' : ''}` : 'Locked — click to unlock and edit'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '10px',
        padding: '4px 12px 4px 6px',
        borderRadius: '999px',
        cursor: 'pointer',
        border: '1px solid ' + (editing ? 'rgba(31,157,85,0.55)' : 'rgba(194,59,59,0.55)'),
        background: editing ? 'rgba(31,157,85,0.12)' : 'rgba(194,59,59,0.10)',
        transition: 'background 0.15s, border-color 0.15s',
      }}
    >
      {/* Sliding switch: knob to the right (green) when editing, left (red) when locked. */}
      <span style={{ position: 'relative', width: '42px', height: '22px', borderRadius: '999px', flexShrink: 0, background: editing ? GREEN : RED, transition: 'background 0.15s' }}>
        <span style={{ position: 'absolute', top: '2px', left: editing ? '22px' : '2px', width: '18px', height: '18px', borderRadius: '50%', background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.35)', transition: 'left 0.15s' }} />
      </span>
      {/* Status text beside the switch, green when editing / red when locked. */}
      <span style={{ fontSize: '0.82em', fontWeight: 700, letterSpacing: '0.02em', color: editing ? GREEN : RED, whiteSpace: 'nowrap' }}>
        {editing ? unlockedLabel : lockedLabel}
        {editing && autoLockHint && <span style={{ fontWeight: 500, color: 'var(--text-muted)' }}> · auto-locks 30s</span>}
      </span>
    </button>
  );
};

export default LockToggle;
